// @ts-ignore
import { Button } from '@vibe/core';
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { getAuthorizeUrl } from '../services/portal';
import { extractErrorMessage } from '../lib/utils';

// The authorize URL comes from the backend — see getAuthorizeUrl. That is a round trip before the
// redirect, so callers disable their control meanwhile: a second click would start a second
// authorization and overwrite the first one's PKCE record.
function useStartAuthorization(boardId: number, slug?: string) {
  const [isStarting, setIsStarting] = useState(false);

  function start() {
    setIsStarting(true);

    getAuthorizeUrl(boardId, slug)
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch((err) => {
        // Nothing navigates on failure, so without this the button just flickers and the manager is
        // left guessing. The backend answers 503 OAUTH_AUTHORIZE_URL_FAILED when it cannot store the
        // PKCE record, which is a "try again in a moment", not a dead end.
        enqueueSnackbar(extractErrorMessage(err), { variant: 'error' });
        setIsStarting(false);
      });
  }

  return { start, isStarting };
}

type RegisterAsPortalManagerProps = {
  boardId: number;
  color?: boolean;
  label?: string;
  slug?: string;
};

// The one way to authorize a portal, and it always runs the flow.
//
// It used to skip authorization when the manager's token already carried every required scope, and
// call `set_manager` instead to point the portal at that user. Under the legacy flow that was right —
// a token that never expired gained nothing from being reissued — but under OAuth 2.1 the flow itself
// is the point: it is how a portal still on a legacy token moves onto a rotating pair, and how a
// manager recovers a pair confirmed dead while its access token still works.
//
// Nothing is lost by always taking the long way round. The OAuth callback verifies board ownership
// and writes the same `manageruserid` key that `set_manager` did, so the end state is identical — it
// just also returns a fresh pair.
export function RegisterAsPortalManager({ boardId, color, label, slug }: RegisterAsPortalManagerProps) {
  const { start, isStarting } = useStartAuthorization(boardId, slug);

  // Callers pass `mondayContext?.boardId!`, which is genuinely undefined until the monday context
  // resolves. Sending that produces a 400 from the backend validator — JSON.stringify drops the key
  // entirely — so stay disabled until there is a board id to authorize against.
  return (
    <Button kind={color ? 'primary' : 'secondary'} size="medium" disabled={isStarting || !boardId} onClick={start}>
      {label ?? 'Authorize'}
    </Button>
  );
}
